// This optional code is used to register a service worker.
// register() is not called by default.

// This lets the app load faster on subsequent visits in production, and gives
// it offline capabilities. However, it also means that developers (and users)
// will only see deployed updates on subsequent visits to a page, after all the
// existing tabs open on the page have been closed, since previously cached
// resources are updated in the background.

// To learn more about the benefits of this model and instructions on how to
// opt-in, read http://bit.ly/CRA-PWA

function askWaitingWorkerToSkipWaiting(registration) {
  if (registration && registration.waiting) {
    registration.waiting.postMessage({ type: 'SKIP_WAITING' });
  }
}

export function register(config) {
  registerAndWait(config);
}

/**
 * Register the service worker and resolve once the initial update check is done
 * (no waiting worker, first install, or user responded to an update prompt).
 * Later onupdatefound events still call config.onUpdate but do not block the promise.
 */
export function registerAndWait(config = {}) {
  return new Promise(resolveReady => {
    if (!('serviceWorker' in navigator)) {
      resolveReady();
      return;
    }

    const publicUrl = new URL(process.env.PUBLIC_URL, window.location.href);
    if (publicUrl.origin !== window.location.origin) {
      console.log(
        '__SW_LOG:  service worker wont work if PUBLIC_URL is on a different origin'
      );
      resolveReady();
      return;
    }

    let bootstrapDone = false;
    const finishBootstrap = () => {
      if (bootstrapDone) return;
      bootstrapDone = true;
      resolveReady();
    };

    const handleUpdate = registration => {
      if (!bootstrapDone) {
        if (!registration?.waiting) {
          finishBootstrap();
          return;
        }
        const result = config.onUpdate?.(registration, { atStartup: true });
        if (result && typeof result.then === 'function') {
          result.then(finishBootstrap).catch(finishBootstrap);
        } else if (!config.onUpdate) {
          askWaitingWorkerToSkipWaiting(registration);
          finishBootstrap();
        } else {
          finishBootstrap();
        }
        return;
      }

      if (config.onUpdate) {
        config.onUpdate(registration, { atStartup: false });
      } else {
        askWaitingWorkerToSkipWaiting(registration);
      }
    };

    const wrappedConfig = {
      ...config,
      onUpdate: handleUpdate,
      onNoUpdate: registration => {
        config.onNoUpdate?.(registration);
        finishBootstrap();
      },
      onSuccess: registration => {
        config.onSuccess?.(registration);
        finishBootstrap();
      }
    };

    let refreshing = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (refreshing) return;
      refreshing = true;
      console.log('__SW_LOG: controllerchange -> reload');
      window.location.reload();
    });

    const run = () => {
      const swUrl = `${process.env.PUBLIC_URL}/service-worker.js`;

      registerValidSW(swUrl, wrappedConfig).catch(error => {
        console.error(
          '__SW_LOG: Error during service worker registration:',
          error
        );
        finishBootstrap();
      });
    };

    if (document.readyState === 'complete') {
      run();
    } else {
      window.addEventListener('load', run, { once: true });
    }
  });
}

function registerValidSW(swUrl, config) {
  console.log('__SW_LOG:  Start SW register.');

  return navigator.serviceWorker
    .register(swUrl)
    .then(
      registration => {
        // Already waiting from a previous visit — activate immediately.
        if (registration.waiting) {
          console.log('__SW_LOG: waiting worker found on register');
          if (config && config.onUpdate) {
            config.onUpdate(registration);
          } else {
            askWaitingWorkerToSkipWaiting(registration);
          }
        } else if (config && config.onNoUpdate) {
          config.onNoUpdate(registration);
        }

        registration.onupdatefound = () => {
          console.log('__SW_LOG: Update found');
          const installingWorker = registration.installing;
          if (installingWorker == null) {
            console.log('__SW_LOG: installingWorker is null');
            return;
          }
          installingWorker.onstatechange = () => {
            if (installingWorker.state === 'installed') {
              if (navigator.serviceWorker.controller) {
                // New content fetched; activate + reload so users never mix chunks.
                console.log(
                  '__SW_LOG: New content is available; activating update.'
                );

                if (config && config.onUpdate) {
                  config.onUpdate(registration);
                } else {
                  askWaitingWorkerToSkipWaiting(registration);
                }
              } else {
                console.log('__SW_LOG:  Content is cached for offline use.');

                if (config && config.onSuccess) {
                  config.onSuccess(registration);
                }
              }
            }
          };
        };
      },
      reason => {
        console.error('__SW_LOG: Registration rejected:', reason);
        throw reason;
      }
    )
    .catch(error => {
      console.error(
        '__SW_LOG: Error during service worker registration:',
        error
      );
      throw error;
    });
}

export function unregister() {
  if (!('serviceWorker' in navigator)) {
    return;
  }

  navigator.serviceWorker.getRegistrations().then(registrations => {
    registrations.forEach(registration => {
      registration.unregister();
    });
  });

  clearServiceWorkerCaches();
}

export function clearServiceWorkerCaches() {
  if (!('caches' in window)) {
    return Promise.resolve();
  }
  return caches
    .keys()
    .then(keys => Promise.all(keys.map(key => caches.delete(key))));
}

/**
 * Snapshot of service worker health for diagnostics (e.g. About dialog).
 * @returns {Promise<{ state: string, label: string }>}
 */
export async function getServiceWorkerStatus() {
  if (!('serviceWorker' in navigator)) {
    return { state: 'unsupported', label: 'Not supported on this browser' };
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    if (!registrations.length) {
      return {
        state: 'missing',
        label: window.standalone
          ? 'Not installed'
          : 'Not installed (registers in standalone; updates if SW controls page)'
      };
    }

    const registration =
      registrations.find(r => r.active || r.waiting || r.installing) ||
      registrations[0];
    const controller = navigator.serviceWorker.controller;

    if (registration.installing) {
      return { state: 'installing', label: 'Installing…' };
    }
    if (registration.waiting) {
      return {
        state: 'waiting',
        label: controller
          ? 'Update waiting (tap New Start to apply)'
          : 'Installed, waiting to activate'
      };
    }
    if (registration.active) {
      if (controller) {
        return { state: 'ok', label: 'Installed and working' };
      }
      return {
        state: 'uncontrolled',
        label: 'Installed but not controlling this page'
      };
    }

    return { state: 'unknown', label: 'Unknown state' };
  } catch (error) {
    return {
      state: 'error',
      label: `Problem: ${error?.message || 'check failed'}`
    };
  }
}
